import { apiClient, type Paginated } from '@/core/api';
import type { ActivityType } from './constants';
import type { Activity, ActivityInput } from './types';

export const BASE = '/crm/activities/';

export interface ActivityListParams {
  page?: number;
  search?: string;
  type?: ActivityType;
  lead?: number;
  company?: number;
  contact?: number;
  deal?: number;
  owner?: number;
  /** `true` → only archived rows; `'all'` → both. Unset/`false` → active only. */
  archived?: boolean | 'all';
  /** DRF `OrderingFilter` — e.g. `occurred_at` or `-occurred_at`. */
  ordering?: string;
  /** Server-side "my activities" filter; backend reads the requesting user. */
  mine?: boolean;
  /** Occurred-window (DateTime) — ISO bounds; `_before` should be end-of-day. */
  occurred_at_after?: string;
  occurred_at_before?: string;
}

export async function listActivities(params: ActivityListParams = {}): Promise<Paginated<Activity>> {
  const { data } = await apiClient.get<Paginated<Activity>>(BASE, { params });
  return data;
}

export async function createActivity(input: ActivityInput): Promise<Activity> {
  const { data } = await apiClient.post<Activity>(BASE, input);
  return data;
}

export async function updateActivity(id: number, input: Partial<ActivityInput>): Promise<Activity> {
  const { data } = await apiClient.patch<Activity>(`${BASE}${id}/`, input);
  return data;
}

/**
 * Soft-archive an activity. The backend keeps the row, flips `is_archived=true`,
 * and hides it from default list queries. Use {@link restoreActivity} to reverse.
 */
export async function archiveActivity(id: number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function restoreActivity(id: number): Promise<Activity> {
  const { data } = await apiClient.post<Activity>(`${BASE}${id}/restore/`);
  return data;
}

/** Permanently delete an archived activity (hard delete; admin only, irreversible). */
export async function purgeActivity(id: number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/purge/`);
}
